import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play, SkipBack, SkipForward, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { HAND_COLORS } from './board-grid/roles';
import { MAX_DWELL, MIN_DWELL, type CircuitRun } from './circuit-run';
import type { BoardHand } from '../../types/device';

interface CircuitPlayerBarProps {
  /** The run this bar drives — see useCircuitRun. */
  run: CircuitRun;
  /**
   * Which hand the hold the run is on is taken with, on a circuit that forces
   * them. Said in a word here, for a climber reading the phone between moves.
   */
  currentHand?: BoardHand | null;
  /**
   * What the Play button does before the run begins, where something has to
   * happen first: a board is claimed, so nobody else's run is cut in on by
   * accident. Left out, the run simply begins.
   */
  onStart?: () => void;
  /** Whether that is still going on, so Play cannot be pressed twice. */
  starting?: boolean;
}

/**
 * The bar a circuit is walked from: stop, prev, play/pause, next, and where the
 * run has got to.
 *
 * It sits between the picture and the row under it with the play button
 * straddling the two, so the one control the climber reaches for mid-problem is
 * the biggest thing on the page. Press it cold and it asks how the run should go
 * — on its own, a hold every few seconds, or stepped by hand — because those are
 * two different ways of using a circuit and the answer is worth keeping for the
 * length of it.
 *
 * One bar for every wall: the board's LEDs and a photo of a wall differ in what
 * they do with the hold the run is on, not in how the run is walked. See
 * `useCircuitRun`, which is the other half of this.
 */
export default function CircuitPlayerBar({
  run,
  currentHand = null,
  onStart,
  starting = false,
}: CircuitPlayerBarProps) {
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const start = () => {
    setSettingsOpen(false);
    if (onStart) return onStart();

    run.begin();
  };

  return (
    <>
      {/* The bar the play button straddles: the button is taller than the bar,
          so it sits over the photo above and the row below. */}
      <div className="relative flex h-11 items-center justify-center gap-20 border-b bg-background">
        {run.running && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => run.stop()}
            aria-label={t('climbs.board.circuit.stop')}
            className="absolute left-2 text-muted-foreground"
          >
            <Square className="size-4 fill-current" aria-hidden />
          </Button>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => run.step !== null && run.stepTo(run.step - 1)}
          disabled={!run.canGoBack}
          aria-label={t('climbs.board.circuit.previous')}
        >
          <SkipBack className="size-5" aria-hidden />
        </Button>

        <button
          type="button"
          onClick={() => {
            if (!run.running) setSettingsOpen(true);
            else if (run.paused) run.resume();
            else run.pause();
          }}
          aria-label={run.playing ? t('climbs.board.circuit.pause') : t('climbs.board.circuit.play')}
          className="absolute -top-4 left-1/2 z-10 flex size-16 -translate-x-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-background transition-transform active:scale-95"
          data-testid="circuit-play-button"
        >
          {run.playing ? (
            <Pause className="size-7 fill-current" aria-hidden />
          ) : (
            <Play className="size-7 translate-x-0.5 fill-current" aria-hidden />
          )}
        </button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => run.step !== null && run.stepTo(run.step + 1)}
          disabled={!run.canGoOn}
          aria-label={t('climbs.board.circuit.next')}
        >
          <SkipForward className="size-5" aria-hidden />
        </Button>

        {/* Where the run is, for a climber who looks down mid-circuit. */}
        {run.step !== null && (
          <span className="absolute right-4 flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
            {currentHand && (
              <span
                className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                style={{ backgroundColor: HAND_COLORS[currentHand] }}
                title={t(`climbs.board.hands.${currentHand}`)}
              >
                {t(`climbs.board.handsShort.${currentHand}`)}
              </span>
            )}
            {/* Counted in moves, which is what the run is a list of: a hold used
                twice is two of them, a foot is none of them, and the count agrees
                with the numbers on the picture above it. */}
            {t('climbs.board.circuit.progress', {
              current: run.step + 1,
              total: run.steps.length,
            })}
          </span>
        )}
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('climbs.board.circuit.playTitle')}</DialogTitle>
            <DialogDescription>{t('climbs.board.circuit.playDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="circuit-autoplay">{t('climbs.board.circuit.autoplay')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('climbs.board.circuit.autoplayHint')}
                </p>
              </div>
              <Switch
                id="circuit-autoplay"
                checked={run.autoplay}
                onCheckedChange={run.setAutoplay}
                aria-label={t('climbs.board.circuit.autoplay')}
              />
            </div>

            {/* Only autoplay waits, so the dwell is only worth setting with it on. */}
            <div className={`space-y-2 ${run.autoplay ? '' : 'opacity-50'}`}>
              <Label>{t('climbs.board.circuit.dwell', { seconds: run.dwell })}</Label>
              <Slider
                min={MIN_DWELL}
                max={MAX_DWELL}
                step={1}
                value={[run.dwell]}
                onValueChange={([value]) => run.setDwell(value)}
                disabled={!run.autoplay}
                aria-label={t('climbs.board.circuit.dwell', { seconds: run.dwell })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={start} disabled={starting}>
              {t('climbs.board.circuit.play')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
